import React, { useState } from 'react';
import PropTypes from 'prop-types';

import { makeStyles } from '@material-ui/core/styles';
import Button from '@material-ui/core/Button';
import TextField from '@material-ui/core/TextField';

import Modal from 'components/Modal';
import PickList from 'components/PickList';
import { useGetApi, ServiceRequestAPI } from 'providerSite/api';
import ColorButton from './ColorButton';


const CONTACT_ATTEMPT_REASONS = [
  'Couldn’t get in contact via any channel (phone, sms, email)',
  'Didn’t pickup any phone calls',
];

const CONTACT_ATTEMPT_OPTIONS = ['1', '2', '3', '4+'];

const OTHER_REASON = 'Other (please specify)';

// Shown when the provider selects "4+" attempts.
const TOO_MANY_ATTEMPTS_MESSAGE =
  'We don\'t recommend contacting participants more than three times';

const useStyles = makeStyles(theme => ({
  modal: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  backdrop: {
    background: 'none',
  },
  paper: {
    backgroundColor: 'rgba(255, 255, 255, 0.5)',
    backdropFilter: 'blur(20px)',
    boxShadow: theme.shadows[5],
    borderRadius: '12px',
    width: '540px',
    marginLeft: '200px',
    marginTop: '-200px',
    [theme.breakpoints.down('sm')]: {
      margin: '0 30px',
    },
  },
  container: {
    padding:'20px 20px',
  },
  header: {
    fontSize: '20px',
    fontWeight: '500',
    color: theme.palette.warning.main,
    padding: '20px 30px 15px 30px',
    borderBottom: '1px solid #e2e2e2'
  },
  question: {
    fontSize: '14px',
    fontWeight: '400',
    color: theme.palette.text.primary,
    marginBottom: '5px',
    textShadow: '1px 1px 2px #dfdfdf',
  },
  warning: {
    color: theme.palette.error.main,
    fontSize: '14px',
    marginTop: '5px',
  },
  buttonContainer:{
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-end',
    gap: '20px',
    margin: '10px 0',
  }
}));

const UnsuccessfulReportModal = ({ isOpen, onClose, onSubmit }) => {
  const classes = useStyles();

  const [reason, setReason] = useState('');
  const [comment, setComment] = useState('');
  const [attempts, setAttempts] = useState(null);

  const { data: reasons = [], isLoading: isLoadingReasons } = useGetApi(
    ServiceRequestAPI.GET_UNSUCCESSFUL_REASONS,
    {staleTimeMinute: 60, enabled: isOpen});

  const selectedReason = reasons.find(item => item.reason === reason);
  const showAttempts = CONTACT_ATTEMPT_REASONS.includes(reason);
  const isCommentRequired = reason === OTHER_REASON;
  const isDisabled = !reason
    || (showAttempts && !attempts)
    || (isCommentRequired && !comment.trim());

  const handleReason = (value) => {
    setReason(value);
    setAttempts(null);
  };

  const handleClose = () => {
    onClose();
    setComment('');
    setReason('');
    setAttempts(null);
  };

  const handleSubmit = () => {
    onSubmit({
      reasonId: selectedReason ? selectedReason.id : null,
      attempts: showAttempts ? attempts : null,
      comment,
    });
    handleClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      modalClassName={classes.modal}
      backdropClassName={classes.backdrop}
      paperClassName={classes.paper}
    >
      <div className={classes.header}>Thanks for letting us know!</div>
      <div className={classes.container}>
        <div className={classes.question}>
          What was the reason you are marking this as unsuccessful:
        </div>
        <div style={{marginTop: '-5px'}}>
          <PickList
            key={isLoadingReasons ? 'loading' : 'loaded'}
            title="Reason"
            availableOptions={reasons.map(item => item.reason)}
            onSelect={handleReason}
            hideOther
          />
          {showAttempts && (
            <>
              <div className={classes.question}>
                How many attempts did you make to contact them?
              </div>
              <PickList
                key={reason}
                title="Attempts"
                availableOptions={CONTACT_ATTEMPT_OPTIONS}
                onSelect={setAttempts}
                hideOther
              />
              {attempts === '4+' && (
                <div className={classes.warning}>{TOO_MANY_ATTEMPTS_MESSAGE}</div>
              )}
            </>
          )}
        </div>
        <div className={classes.question}>
          {isCommentRequired ?
            'Please tell us the reason so we can review this request.' :
            'Would you like to leave a comment about this request for review by Clickability.'}
        </div>
        <TextField
          variant="outlined"
          InputProps={{ style: { borderRadius: '10px', marginTop: '5px' } }}

          fullWidth
          multiline
          rows={4}
          value={comment}
          placeholder={isCommentRequired ? 'Required' : 'Optional'}
          onChange={(e) => setComment(e.target.value)}
        />
        <div className={classes.buttonContainer}>
          <ColorButton
            style={{width: '100px'}}
            onClick={handleSubmit}
            disabled={isDisabled}
            disableElevation
          >
          Submit
          </ColorButton>
          <Button
            style={{borderRadius: '8px', marginLeft: '8px', width: '100px'}}
            onClick={handleClose}
            variant='outlined'
            disableElevation
          >
            Cancel
          </Button>
        </div>
      </div>
    </Modal>
  );
};

UnsuccessfulReportModal.propTypes = {
  isOpen:PropTypes.bool,
  onClose:PropTypes.func.isRequired,
  onSubmit:PropTypes.func.isRequired,
};

export default UnsuccessfulReportModal;
